import React from 'react';
import { FormattedMessage } from 'react-intl';
import StarRating, { Rating } from '@alltrails/shared/components/StarRating';
import useLanguageRegionCode from 'hooks/useLanguageRegionCode';
import { FilterConfigStarRating } from 'types/Search';

export type StarRatingGroupProps = {
  config: FilterConfigStarRating;
  handler: (changes: FilterConfigStarRating) => void;
};

const generateRatings = (values: number[], formatter: Intl.NumberFormat) => {
  return values.map(value => {
    const formattedValue = formatter.format(value);

    return {
      value: value === null ? null : formattedValue,
      label: value === null ? <FormattedMessage defaultMessage="0+" /> : <FormattedMessage defaultMessage="Over {value}" values={{ value: formattedValue }} />,
      hasStar: value !== null
    };
  });
};

const StarRatingGroup = ({ config, handler }: StarRatingGroupProps) => {
  const languageRegionCode = useLanguageRegionCode();

  const formatter = new Intl.NumberFormat(languageRegionCode, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1
  });

  const ratings: Rating[] = generateRatings([null, 3.5, 4, 4.5], formatter);

  const valueToIndex = (value: FilterConfigStarRating): number => {
    if (value === null) {
      return 0;
    }

    const index = ratings.findIndex(r => r.value === value);

    return index !== -1 ? index : 0;
  };

  const indexToValue = (index: number): string => ratings[index].value;

  return (
    <StarRating
      anyRatingLabel={<FormattedMessage defaultMessage="Any" />}
      selectedIndex={valueToIndex(config)}
      title={<FormattedMessage defaultMessage="Rating" />}
      onChange={index => handler(indexToValue(index))}
      ratings={ratings}
    />
  );
};

export default StarRatingGroup;
